import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { RHFTextField } from '../../../../components/hook-form';
import AddCanvas from './AddCanvas';
import { useFormContext } from 'react-hook-form';
import { IExaminationSchema } from '../../../../@types/examination';
import { PreviousRecords } from './PreviousRecords';

/**
 * Mirrors the backend column widths (see `EXAMINATION_NOTES_MAX_LENGTH` in
 * `optopro-backend/src/examination/examination.constants.ts`). Without these
 * caps a long comment reached MySQL and failed with "Data too long", which the
 * screen showed as a one-letter error (client bug 33).
 */
const EXAMINATION_NOTES_MAX_LENGTH = 2000;
const EXAMINATION_FIELD_MAX_LENGTH = 200;

type Props = {
  clinicalRecordId: number;
  setExamination: (data: IExaminationSchema) => void;
};

const External = ({ clinicalRecordId, setExamination }: Props) => {
  const [openCanvasSide, setOpenCanvasSide] = useState<string>('');
  const [openRecordsModal, setOpenRecordsModal] = useState(false);
  const [leftCanvas, setLeftCanvas] = useState('');
  const [rightCanvas, setRightCanvas] = useState('');

  const handleOpenRecordModal = () => {
    setOpenRecordsModal(true);
  };

  const handleCloseRecordModal = () => {
    setOpenRecordsModal(false);
  };

  const handleCanvasModal = (side: string) => {
    if (side === 'left') {
      const leftVisualImage = getValues('externalEyeHealth.leftVisualImage');
      if (leftVisualImage && !leftCanvas) setLeftCanvas(leftVisualImage);
    }
    if (side === 'right') {
      const rightVisualImage = getValues('externalEyeHealth.rightVisualImage');
      if (rightVisualImage && !rightCanvas) setRightCanvas(rightVisualImage);
    }

    setOpenCanvasSide(side);
  };

  const handleCloseCanvasModal = () => {
    setOpenCanvasSide('');
  };

  const { setValue, getValues, watch } = useFormContext();

  const leftVisualImage = watch('externalEyeHealth.leftVisualImage') as
    string | undefined;
  const rightVisualImage = watch('externalEyeHealth.rightVisualImage') as
    string | undefined;

  const hasLeftEyeImage = Boolean(
    typeof leftVisualImage === 'string' && leftVisualImage.trim(),
  );
  const hasRightEyeImage = Boolean(
    typeof rightVisualImage === 'string' && rightVisualImage.trim(),
  );

  const clearExternalData = () => {
    const fieldNames = [
      'externalEyeHealth.leftConjuctiva',
      'externalEyeHealth.leftLidsMargin',
      'externalEyeHealth.leftCornea',
      'externalEyeHealth.leftTears',
      'externalEyeHealth.leftAnteriorChamber',
      'externalEyeHealth.leftLimbus',
      'externalEyeHealth.leftIris',
      'externalEyeHealth.leftPupilSize',
      'externalEyeHealth.leftRAPD',
      'externalEyeHealth.leftConvergence',
      'externalEyeHealth.leftMotility',
      'externalEyeHealth.leftConfrontation',
      'externalEyeHealth.leftAdditionalNotes',
      'externalEyeHealth.rightConjuctiva',
      'externalEyeHealth.rightLidsMargin',
      'externalEyeHealth.rightCornea',
      'externalEyeHealth.rightTears',
      'externalEyeHealth.rightAnteriorChamber',
      'externalEyeHealth.rightLimbus',
      'externalEyeHealth.rightIris',
      'externalEyeHealth.rightPupilSize',
      'externalEyeHealth.rightRAPD',
      'externalEyeHealth.rightConvergence',
      'externalEyeHealth.rightMotility',
      'externalEyeHealth.rightConfrontation',
      'externalEyeHealth.rightAdditionalNotes',
    ];

    const clearedValues = { ...getValues() };
    fieldNames.forEach((fieldName) => {
      clearedValues[fieldName] = '';
    });

    Object.keys(clearedValues).forEach((fieldName) => {
      setValue(fieldName, clearedValues[fieldName]);
    });
  };

  const copyValues = () => {
    const leftConjuctiva = getValues('externalEyeHealth.leftConjuctiva');
    const leftAnteriorChamber = getValues(
      'externalEyeHealth.leftAnteriorChamber',
    );
    const leftLidsMargin = getValues('externalEyeHealth.leftLidsMargin');
    const leftCornea = getValues('externalEyeHealth.leftCornea');
    const leftTears = getValues('externalEyeHealth.leftTears');
    const leftIris = getValues('externalEyeHealth.leftIris');
    const leftLimbus = getValues('externalEyeHealth.leftLimbus');
    const leftPupilSize = getValues('externalEyeHealth.leftPupilSize');
    const leftRAPD = getValues('externalEyeHealth.leftRAPD');
    const leftConvergence = getValues('externalEyeHealth.leftConvergence');
    const leftMotility = getValues('externalEyeHealth.leftMotility');
    const leftConfrontation = getValues('externalEyeHealth.leftConfrontation');
    const leftAdditionalNotes = getValues(
      'externalEyeHealth.leftAdditionalNotes',
    );

    setValue('externalEyeHealth.rightConjuctiva', leftConjuctiva);
    setValue('externalEyeHealth.rightCornea', leftCornea);
    setValue('externalEyeHealth.rightLidsMargin', leftLidsMargin);
    setValue('externalEyeHealth.rightTears', leftTears);
    setValue('externalEyeHealth.rightAnteriorChamber', leftAnteriorChamber);
    setValue('externalEyeHealth.rightIris', leftIris);
    setValue('externalEyeHealth.rightLimbus', leftLimbus);
    setValue('externalEyeHealth.rightPupilSize', leftPupilSize);
    setValue('externalEyeHealth.rightRAPD', leftRAPD);
    setValue('externalEyeHealth.rightConvergence', leftConvergence);
    setValue('externalEyeHealth.rightMotility', leftMotility);
    setValue('externalEyeHealth.rightConfrontation', leftConfrontation);
    setValue('externalEyeHealth.rightAdditionalNotes', leftAdditionalNotes);
  };

  const setExternalDefaultValue = () => {
    setValue('externalEyeHealth.rightConjuctiva', 'clear');
    setValue('externalEyeHealth.rightCornea', 'clear');
    setValue('externalEyeHealth.rightLidsMargin', 'lashes clear');
    setValue('externalEyeHealth.rightTears', 'clear');
    setValue('externalEyeHealth.rightAnteriorChamber', 'clear');
    setValue('externalEyeHealth.rightIris', 'normal');
    setValue('externalEyeHealth.rightLimbus', 'clear');
    setValue('externalEyeHealth.rightPupilSize', 'normal');
    setValue('externalEyeHealth.rightRAPD', 'PERRL');
    setValue('externalEyeHealth.rightConvergence', 'within normal limits');
    setValue('externalEyeHealth.rightMotility', 'full');
    setValue('externalEyeHealth.rightConfrontation', 'good');
    setValue('externalEyeHealth.rightAdditionalNotes', '');
    setValue('externalEyeHealth.leftConjuctiva', 'clear');
    setValue('externalEyeHealth.leftCornea', 'clear');
    setValue('externalEyeHealth.leftLidsMargin', 'lashes clear');
    setValue('externalEyeHealth.leftTears', 'clear');
    setValue('externalEyeHealth.leftAnteriorChamber', 'clear');
    setValue('externalEyeHealth.leftIris', 'normal');
    setValue('externalEyeHealth.leftLimbus', 'clear');
    setValue('externalEyeHealth.leftPupilSize', 'normal');
    setValue('externalEyeHealth.leftRAPD', 'PERRL');
    setValue('externalEyeHealth.leftConvergence', 'within normal limits');
    setValue('externalEyeHealth.leftMotility', 'full');
    setValue('externalEyeHealth.leftConfrontation', 'good');
    setValue('externalEyeHealth.leftAdditionalNotes', '');
  };

  useEffect(() => {
    if (rightCanvas) {
      setValue('externalEyeHealth.rightVisualImage', rightCanvas);
    }
    if (leftCanvas) {
      setValue('externalEyeHealth.leftVisualImage', leftCanvas);
    }
  }, [rightCanvas, leftCanvas]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="right"
        justifyContent="right"
        ml={1}
        mr={1}
        spacing={2}
      >
        <Button variant="contained" onClick={clearExternalData}>
          Clear
        </Button>
        <Button variant="contained" onClick={setExternalDefaultValue}>
          Default
        </Button>
        <Button variant="contained" onClick={copyValues}>
          Copy R to L
        </Button>
        <Button variant="contained" onClick={handleOpenRecordModal}>
          Previous Records
        </Button>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
        p={5}
      >
        <Box
          sx={{
            width: '33.33%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography color={'#637381'} variant="h6" mb={3}>
            Right
          </Typography>
          <RHFTextField
            name="externalEyeHealth.leftConjuctiva"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftLidsMargin"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftCornea"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftTears"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftLimbus"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftAnteriorChamber"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftIris"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftPupilSize"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftRAPD"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftConvergence"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftMotility"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftConfrontation"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.leftAdditionalNotes"
            size="small"
            multiline
            rows={4}
            inputProps={{ maxLength: EXAMINATION_NOTES_MAX_LENGTH }}
            helperText={`Up to ${EXAMINATION_NOTES_MAX_LENGTH} characters`}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <Badge
            color="success"
            variant="dot"
            invisible={!hasLeftEyeImage}
            sx={{
              width: '90%',
              mb: '10px',
              '& .MuiBadge-badge': { top: 8, right: 16 },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleCanvasModal('left')}
              sx={{ width: '100%' }}
            >
              Images
            </Button>
          </Badge>
        </Box>
        <Box
          sx={{
            width: '33.33%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '60px',
          }}
        >
          <Typography color={'#637381'} variant="h6" mb={3}></Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Conjuctiva
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Lid margins
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Cornea
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Tears
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Limbus
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Anterior Chamber
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Iris
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Pupil Size
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            RAPD
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Acc/Convergence
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Motility
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={3}>
            Confrontation
          </Typography>
          <Typography color={'#637381'} variant="h6" mt={9}>
            Additional Notes
          </Typography>
        </Box>
        <Box
          sx={{
            width: '33.33%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography color={'#637381'} variant="h6" mb={3}>
            Left
          </Typography>
          <RHFTextField
            name="externalEyeHealth.rightConjuctiva"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.rightLidsMargin"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.rightCornea"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.rightTears"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.rightLimbus"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.rightAnteriorChamber"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.rightIris"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <RHFTextField
            name="externalEyeHealth.rightPupilSize"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />{' '}
          <RHFTextField
            name="externalEyeHealth.rightRAPD"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />{' '}
          <RHFTextField
            name="externalEyeHealth.rightConvergence"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />{' '}
          <RHFTextField
            name="externalEyeHealth.rightMotility"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />{' '}
          <RHFTextField
            name="externalEyeHealth.rightConfrontation"
            size="small"
            inputProps={{ maxLength: EXAMINATION_FIELD_MAX_LENGTH }}
            sx={{ width: '90%', marginBottom: '10px' }}
          />{' '}
          <RHFTextField
            name="externalEyeHealth.rightAdditionalNotes"
            size="small"
            multiline
            rows={4}
            inputProps={{ maxLength: EXAMINATION_NOTES_MAX_LENGTH }}
            helperText={`Up to ${EXAMINATION_NOTES_MAX_LENGTH} characters`}
            sx={{ width: '90%', marginBottom: '10px' }}
          />
          <Badge
            color="success"
            variant="dot"
            invisible={!hasRightEyeImage}
            sx={{
              width: '90%',
              mb: '10px',
              '& .MuiBadge-badge': { top: 8, right: 16 },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleCanvasModal('right')}
              sx={{ width: '100%' }}
            >
              Images
            </Button>
          </Badge>
        </Box>
        {openCanvasSide !== '' && (
          <Dialog
            fullWidth
            maxWidth="md"
            open={Boolean(openCanvasSide)}
            onClose={handleCloseCanvasModal}
          >
            <DialogTitle>Examination image</DialogTitle>
            {/* The body needs DialogContent for its padding and scrolling, and
                an explicit Close action: without them the popup rendered with
                the image pressed against the title and no way out but the
                backdrop (client bug 32). The stored URL is read from the form,
                which is the single source of truth after an upload or a
                removal. */}
            <DialogContent dividers>
              <AddCanvas
                canvas={
                  openCanvasSide === 'left'
                    ? leftVisualImage
                    : openCanvasSide === 'right'
                      ? rightVisualImage
                      : undefined
                }
                side={openCanvasSide}
                setCanvas={(value: string) =>
                  openCanvasSide === 'left'
                    ? setLeftCanvas(value)
                    : openCanvasSide === 'right'
                      ? setRightCanvas(value)
                      : null
                }
                type="external"
              />
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={handleCloseCanvasModal}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}
        <Dialog
          fullWidth
          maxWidth="md"
          open={openRecordsModal}
          onClose={handleCloseRecordModal}
        >
          <DialogTitle>Previous Records</DialogTitle>

          <PreviousRecords
            selectedRow={clinicalRecordId}
            setPreviousRecordData={setExamination}
            tab={'examination'}
          />
        </Dialog>
      </Box>
    </>
  );
};

export default External;
